import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, IDR, imgUrl } from "@/lib/api";
import { toast } from "sonner";
import { Circle, CheckCircle2, CreditCard, Mail, PackageCheck, ShoppingBag, AlertTriangle, Ticket, Award, Truck } from "lucide-react";

const ORDER_STATUSES = ["NEW","PAYMENT_PENDING","PAID","PROCESSING","PRODUCTION","READY_TO_SHIP","SHIPPED","COMPLETED","CANCELLED","REFUNDED"];
const PROD_STATUSES = ["PENDING","SENT_TO_PRODUCTION","IN_PRODUCTION","COMPLETED"];
const SHIP_STATUSES = ["NOT_SHIPPED","READY","SHIPPED","DELIVERED"];

const F = "block text-[10px] tracking-widest text-neutral-500 mb-1";
const I = "w-full bg-transparent border border-[color:var(--border)] p-2.5 text-sm focus:border-white outline-none";

const EVENT_META = {
  created: { icon: ShoppingBag, label: "Order dibuat", color: "text-neutral-300" },
  discount_applied: { icon: Ticket, label: "Diskon diterapkan", color: "text-emerald-400" },
  payment_paid: { icon: CreditCard, label: "Pembayaran lunas", color: "text-emerald-400" },
  payment_failed: { icon: AlertTriangle, label: "Pembayaran gagal", color: "text-[color:var(--accent)]" },
  discount_used: { icon: Ticket, label: "Kode diskon dipakai", color: "text-emerald-400" },
  loyalty_issued: { icon: Award, label: "Kode loyalty dikirim", color: "text-amber-300" },
  status_change: { icon: CheckCircle2, label: "Status berubah", color: "text-neutral-300" },
  shipping_updated: { icon: Truck, label: "Info pengiriman diperbarui", color: "text-neutral-300" },
  email_order_received: { icon: Mail, label: "Email order-received terkirim", color: "text-sky-300" },
  email_payment_success: { icon: Mail, label: "Email payment-success terkirim", color: "text-sky-300" },
  email_owner_notify: { icon: Mail, label: "Owner dinotifikasi", color: "text-sky-300" },
  email_order_shipped: { icon: Mail, label: "Email pengiriman terkirim", color: "text-sky-300" },
  email_sent: { icon: Mail, label: "Email terkirim", color: "text-sky-300" },
};

function eventDetail(ev) {
  const m = ev.meta || {};
  switch (ev.kind) {
    case "created": return `Rp${(m.total || 0).toLocaleString("id-ID")} · ${m.items} item · via ${m.provider}`;
    case "discount_applied": return `${m.code} · potongan Rp${(m.amount || 0).toLocaleString("id-ID")}`;
    case "payment_paid": return `${m.provider} · Rp${(m.total || 0).toLocaleString("id-ID")}`;
    case "payment_failed": return `${m.status}`;
    case "discount_used": return `${m.code}`;
    case "loyalty_issued": return `${m.code} · ${m.percent_off}% off`;
    case "status_change": return `${m.field}: ${m.from_ || "—"} → ${m.to}`;
    case "shipping_updated": return `${m.courier || "—"} · ${m.tracking || "—"}`;
    default:
      if (m.subject) return `${m.subject} → ${m.to}${m.delivered ? "" : " (gagal)"}`;
      return "";
  }
}

export default function AdminOrderDetail() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [courier, setCourier] = useState("");
  const [tracking, setTracking] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/admin/orders/${id}`);
    setO(data);
    setCourier(data.shipping_courier || "");
    setTracking(data.shipping_tracking || "");
  };
  useEffect(() => { load(); }, [id]);
  if (!o) return <div className="text-neutral-500">Memuat...</div>;

  const patch = async (body) => {
    const { data } = await api.patch(`/admin/orders/${id}`, body);
    setO(data);
    toast.success("Diperbarui");
  };
  const saveShipping = async () => {
    if (saving) return;
    setSaving(true);
    try { await patch({ shipping_courier: courier, shipping_tracking: tracking }); }
    finally { setSaving(false); }
  };

  const cust = o.customer || {};
  const events = (o.events || []).slice().sort((a, b) => (a.at > b.at ? 1 : -1));

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <Link to="/admin/orders" data-testid="back-orders" className="text-[10px] tracking-widest text-neutral-500 hover:text-white">← ORDERS</Link>
          <h1 className="display text-4xl md:text-6xl mt-1">{o.order_number}</h1>
          <div className="text-xs text-neutral-400 mt-1">Dibuat {(o.created_at || "").slice(0,19).replace("T"," ")}</div>
        </div>
        <div className="flex flex-wrap gap-2 text-[10px] tracking-widest">
          <span className="status-badge">PAY: {o.payment_status}</span>
          <span className="status-badge">ORDER: {o.order_status}</span>
          <span className="status-badge">PROD: {o.production_status}</span>
          <span className="status-badge">SHIP: {o.shipping_status}</span>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="border border-[color:var(--border)] p-4">
          <div className="text-[10px] tracking-widest text-neutral-500 mb-3">CUSTOMER</div>
          <div className="text-sm space-y-1">
            <div><strong>{cust.name}</strong></div>
            <div className="text-neutral-400">{cust.email} · {cust.phone}</div>
            <div className="text-neutral-400 pt-2 whitespace-pre-line">{cust.address}</div>
            <div className="text-neutral-400">{cust.district}, {cust.city}, {cust.province} {cust.postal_code}</div>
            {cust.shipping_notes && <div className="text-neutral-500 pt-2 text-xs">Catatan: {cust.shipping_notes}</div>}
          </div>
        </div>

        <div className="border border-[color:var(--border)] p-4">
          <div className="text-[10px] tracking-widest text-neutral-500 mb-3">PAYMENT & PROVIDER</div>
          <div className="text-sm space-y-1">
            <div>Provider: <span className="mono">{o.payment_provider}</span></div>
            <div>Total: <span className="tabular-nums">{IDR(o.total)}</span></div>
            <div>Subtotal: <span className="tabular-nums">{IDR(o.subtotal)}</span></div>
            {o.discount_amount > 0 && <div className="text-emerald-400">Diskon {o.discount_code}: <span className="tabular-nums">− {IDR(o.discount_amount)}</span></div>}
            <div>Ongkir: <span className="tabular-nums">{IDR(o.shipping)}</span></div>
            {o.shipping_origin_city && (
              <div className="text-neutral-400">Dikirim dari {o.shipping_origin_city}, {o.shipping_origin_province}
                {o.shipping_eta_min_days ? ` · ETA ${o.shipping_eta_min_days}–${o.shipping_eta_max_days} hari kerja` : ""}
              </div>
            )}
            {o.paid_at && <div className="text-emerald-400">Terbayar {o.paid_at.slice(0,19).replace("T"," ")}</div>}
          </div>
        </div>
      </div>

      <div className="border border-[color:var(--border)] mt-6">
        <div className="p-3 text-[10px] tracking-widest text-neutral-500 border-b border-[color:var(--border)]">ITEMS</div>
        <table className="w-full text-sm">
          <thead className="text-[10px] tracking-widest text-neutral-500">
            <tr className="border-b border-[color:var(--border)]">
              <th className="p-3 text-left">IMG</th><th className="p-3 text-left">CODE</th><th className="p-3 text-left">PRODUCT</th>
              <th className="p-3 text-left">COLOR/SIZE</th><th className="p-3 text-right">QTY</th><th className="p-3 text-right">UNIT</th><th className="p-3 text-right">SUBTOTAL</th>
            </tr>
          </thead>
          <tbody>
            {o.items.map((it, i) => (
              <tr key={i} className="border-b border-[color:var(--border)]">
                <td className="p-3"><img src={imgUrl(it.image) || "https://placehold.co/60x80/000/fff?text=—"} alt="" className="w-12 h-16 object-cover" /></td>
                <td className="p-3 mono">{it.article_code}</td>
                <td className="p-3">{it.name}</td>
                <td className="p-3">{it.color} · {it.size}</td>
                <td className="p-3 text-right tabular-nums">{it.qty}</td>
                <td className="p-3 text-right tabular-nums">{IDR(it.price)}</td>
                <td className="p-3 text-right tabular-nums">{IDR(it.line_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mt-6">
        <div className="border border-[color:var(--border)] p-4">
          <label className={F}>ORDER STATUS</label>
          <select data-testid="det-order-status" className={I} value={o.order_status} onChange={(e) => patch({ order_status: e.target.value })}>
            {ORDER_STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="border border-[color:var(--border)] p-4">
          <label className={F}>PRODUCTION STATUS</label>
          <select data-testid="det-prod-status" className={I} value={o.production_status} onChange={(e) => patch({ production_status: e.target.value })}>
            {PROD_STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="border border-[color:var(--border)] p-4">
          <label className={F}>SHIPPING STATUS</label>
          <select data-testid="det-ship-status" className={I} value={o.shipping_status} onChange={(e) => patch({ shipping_status: e.target.value })}>
            {SHIP_STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div className="border border-[color:var(--border)] p-4 mt-6">
        <div className="text-[10px] tracking-widest text-neutral-500 mb-3">SHIPPING DETAILS</div>
        <div className="grid md:grid-cols-2 gap-3">
          <div><label className={F}>COURIER</label>
            <input data-testid="det-courier" className={I} value={courier} onChange={(e) => setCourier(e.target.value)} placeholder="JNE / SiCepat / J&T / AnterAja" />
          </div>
          <div><label className={F}>TRACKING NUMBER</label>
            <input data-testid="det-tracking" className={I} value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Nomor resi" />
          </div>
        </div>
        <button data-testid="det-save-shipping" onClick={saveShipping} disabled={saving} className="mt-4 bg-white text-black px-6 py-2 text-xs tracking-widest disabled:opacity-60">{saving ? "MENYIMPAN..." : "SIMPAN PENGIRIMAN"}</button>
        <p className="text-[10px] text-neutral-500 mt-2">Menandai SHIPPED akan mengirim email "Pesanan Dikirim" ke pelanggan (jika email aktif).</p>
      </div>

      {/* TIMELINE */}
      <div className="border border-[color:var(--border)] mt-6" data-testid="order-timeline">
        <div className="p-3 text-[10px] tracking-widest text-neutral-500 border-b border-[color:var(--border)] flex items-center justify-between">
          <span>TIMELINE</span>
          <span className="text-neutral-500">{events.length} event</span>
        </div>
        {events.length === 0 ? (
          <div className="p-6 text-neutral-500 text-sm">Belum ada event tercatat.</div>
        ) : (
          <ol className="p-4 space-y-4">
            {events.map((ev, i) => {
              const meta = EVENT_META[ev.kind] || { icon: Circle, label: ev.kind, color: "text-neutral-400" };
              const Icon = meta.icon;
              return (
                <li key={i} data-testid={`timeline-${ev.kind}`} className="grid grid-cols-[24px,1fr,auto] gap-3 items-start">
                  <span className={`${meta.color} mt-0.5`}><Icon size={16} strokeWidth={1.25} /></span>
                  <div className="min-w-0">
                    <div className="text-sm">{meta.label}</div>
                    <div className="text-[11px] text-neutral-500 mono truncate">{eventDetail(ev)}</div>
                  </div>
                  <div className="text-[10px] text-neutral-500 mono whitespace-nowrap">{(ev.at || "").slice(5,19).replace("T"," ")}</div>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}
